import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Stack, Typography, Modal, styled, Box, Grid } from "@mui/material";
import { background, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Button } from "@/components/elements/Button";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Form } from "@/components/elements/Form";
import { toast } from "@/utils/toast";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { TRecruitmentSourceForm } from "@/features/job/types/RecruitmentSourceForm";
import { createRecruitmentSourceApi } from "@/api/services/main/recruitment-source";
import { setErrorForm, showError } from "@/utils/error";
import { useGetRecruitmentSourceSchema } from "@/features/job/hooks/useGetRecruitmentSourceSchema";

type TCreateRecruitmentSourceModalProps = {
  modal: UseVisibleReturn;
  refetchRecruitmentSources: () => void;
};

export const CreateRecruitmentSourceModal: FCC<TCreateRecruitmentSourceModalProps> = ({ modal, refetchRecruitmentSources }) => {
  const { recruitmentSourceSchema } = useGetRecruitmentSourceSchema();
  const [isSubmit, setIsSubmit] = useState(false);
  const [errorList, setErrorList] = useState<string[]>([]);

  const methods = useForm<TRecruitmentSourceForm>({
    defaultValues: {},
    resolver: yupResolver(recruitmentSourceSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, reset } = methods;

  const onClose = () => {
    setIsSubmit(false);
    setErrorList([]);
    modal.close();
  };

  const onSubmit = async (data: TRecruitmentSourceForm) => {
    try {
      await createRecruitmentSourceApi(data);
      setIsSubmit(true);
      setErrorList([]);
      toast.success(API_SUCCESS_MESSAGE);
      reset();
      refetchRecruitmentSources();
      modal.close();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    } finally {
      setIsSubmit(false);
    }
  };

  return (
    <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <ModalContainer>
        <Form methods={methods} onSubmit={(data) => onSubmit(data)} schema={recruitmentSourceSchema}>
          <Stack
            bgcolor={background.disable}
            height="56px"
            alignItems="flex-start"
            justifyContent="center"
            pl={4}
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            <Typography variant="body14Light" color={text.primary}>
              求人材獲得ルート ｜ 新規登録
            </Typography>
          </Stack>
          <Stack sx={{ p: "24px", gap: "20px" }}>
            {!isSubmit && errorList.length > 0 && (
              <Box borderRadius={rounded.sm}>
                <Typography variant="body14Light" color="#d32f2f">
                  {errorList.map((error, idx) => (
                    <p key={idx}>{error}</p>
                  ))}
                </Typography>
              </Box>
            )}

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormField control={control} name="recruitmentSourceName">
                  <Input placeholder="入力してください" />
                </FormField>
              </Grid>
            </Grid>

          </Stack>
          <Stack direction="row" gap="16px" justifyContent="flex-end" p="16px 32px">
            <Button variant="ghost" onClick={onClose}>
              閉じる
            </Button>
            <Button variant="primary" type="submit">
              求人材獲得ルートを保存
            </Button>
          </Stack>
        </Form>
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  width: 920px;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  max-height: 90vh;
  outline: none;
  &:focus {
    outline: none;
  }
`;
